import React from "react";
import { BlockMath, InlineMath, ReadableInlineMath } from "../../../components/math/KatexWrappers";
import FractionInput from "../../../components/inputs/FractionInput";
import { scoreStaged } from "../../openTaskScoring";

function gcd(a, b) {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y) [x, y] = [y, x % y];
  return x || 1;
}

function normalizeFraction(numerator, denominator) {
  if (denominator < 0) return normalizeFraction(-numerator, -denominator);
  if (numerator === 0) return { numerator: 0, denominator: 1 };
  const divisor = gcd(numerator, denominator);
  return { numerator: numerator / divisor, denominator: denominator / divisor };
}

function lcm(a, b) {
  return Math.abs(a * b) / gcd(a, b);
}

function lcmMany(numbers) {
  return numbers.reduce((acc, value) => lcm(acc, value), 1);
}

function expandTo(fraction, denominator) {
  return {
    numerator: fraction.numerator * (denominator / fraction.denominator),
    denominator,
  };
}

function addFractions(...fractions) {
  return fractions.reduce(
    (sum, fraction) =>
      normalizeFraction(
        sum.numerator * fraction.denominator + fraction.numerator * sum.denominator,
        sum.denominator * fraction.denominator
      ),
    { numerator: 0, denominator: 1 }
  );
}

function fractionLatex(fraction) {
  if (!fraction) return "\\text{--}";
  if (fraction.denominator === 1) return `${fraction.numerator}`;
  if (fraction.numerator < 0) {
    return `-\\frac{${Math.abs(fraction.numerator)}}{${fraction.denominator}}`;
  }
  return `\\frac{${fraction.numerator}}{${fraction.denominator}}`;
}

function parseFraction(raw) {
  if (raw == null) return null;
  const compact = String(raw)
    .trim()
    .replace(",", ".")
    .replace(/[−–]/g, "-")
    .replace(/\s+/g, "");
  const match = compact.match(/^(-?\d+)\/(-?\d+)$/);
  if (match) {
    const numerator = Number(match[1]);
    const denominator = Number(match[2]);
    if (!Number.isInteger(numerator) || !Number.isInteger(denominator) || denominator === 0) return null;
    return normalizeFraction(numerator, denominator);
  }
  if (/^-?\d+$/.test(compact)) return { numerator: Number(compact), denominator: 1 };
  return null;
}

function sameFraction(a, b) {
  return !!a && !!b && a.numerator === b.numerator && a.denominator === b.denominator;
}

function rawValue(value) {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value.raw === "string") return value.raw;
  return "";
}

function setupLooksCorrect(raw, data) {
  const text = String(raw || "")
    .toLowerCase()
    .replace(/,/g, ".")
    .replace(/[−–]/g, "-")
    .replace(/\s+/g, "");
  if (!text) return false;

  const hasTarget = text.includes(`${data.total.numerator}/${data.total.denominator}`);
  const hasFirst = text.includes(`${data.first.numerator}/${data.first.denominator}`);
  const hasSecond = text.includes(`${data.second.numerator}/${data.second.denominator}`);
  const hasVariable = /[x?t]/.test(text);
  const hasResult =
    text.includes(`${data.answer.numerator}/${data.answer.denominator}`) ||
    text.includes(`${data.answer.numerator * (data.commonDenominator / data.answer.denominator)}/${data.commonDenominator}`);
  const hasSubtraction = text.includes("-");
  const hasEquation = text.includes("=");

  return hasTarget && hasFirst && hasSecond && (hasSubtraction || (hasEquation && (hasVariable || hasResult)));
}

function buildData({ first, second, total }) {
  const answer = normalizeFraction(
    total.numerator * first.denominator * second.denominator -
      first.numerator * total.denominator * second.denominator -
      second.numerator * total.denominator * first.denominator,
    total.denominator * first.denominator * second.denominator
  );
  const commonDenominator = lcmMany([first.denominator, second.denominator, total.denominator, answer.denominator]);
  const firstExpanded = expandTo(first, commonDenominator);
  const secondExpanded = expandTo(second, commonDenominator);
  const totalExpanded = expandTo(total, commonDenominator);
  const answerExpanded = expandTo(answer, commonDenominator);
  const knownSumExpanded = {
    numerator: firstExpanded.numerator + secondExpanded.numerator,
    denominator: commonDenominator,
  };

  return {
    taskType: "E8MissingFractionSummandOpenTask",
    first,
    second,
    total,
    answer,
    commonDenominator,
    firstExpanded,
    secondExpanded,
    totalExpanded,
    knownSumExpanded,
    answerExpanded,
    maxPoints: 2,
  };
}

function createOriginalTask() {
  return {
    type: "E8MissingFractionSummandOpenTask",
    data: buildData({
      first: { numerator: 1, denominator: 5 },
      second: { numerator: 1, denominator: 6 },
      total: { numerator: 7, denominator: 15 },
    }),
    source: {
      variant: "original",
      exam: "E8",
      session: "2025_MAJ",
      taskNumber: 16,
    },
    tags: ["e8", "ulamki-zwykle", "dodawanie-odejmowanie", "zadanie-otwarte"],
  };
}

const RANDOM_BLUEPRINTS = [
  { first: [1, 4], second: [1, 5], answerDenominator: 20 },
  { first: [1, 6], second: [1, 8], answerDenominator: 24 },
  { first: [2, 9], second: [1, 6], answerDenominator: 18 },
  { first: [1, 7], second: [2, 21], answerDenominator: 14 },
  { first: [3, 20], second: [1, 10], answerDenominator: 40 },
  { first: [1, 9], second: [1, 12], answerDenominator: 36 },
  { first: [2, 15], second: [1, 10], answerDenominator: 30 },
  { first: [1, 8], second: [3, 16], answerDenominator: 16 },
];

function pick(array) {
  return array[Math.floor(Math.random() * array.length)];
}

function tupleFraction(tuple) {
  return normalizeFraction(tuple[0], tuple[1]);
}

function createRandomTask() {
  const blueprint = pick(RANDOM_BLUEPRINTS);
  const first = tupleFraction(blueprint.first);
  const second = tupleFraction(blueprint.second);
  const answer = { numerator: 1, denominator: blueprint.answerDenominator };
  const total = addFractions(first, second, answer);

  return {
    type: "E8MissingFractionSummandOpenTask",
    data: buildData({ first, second, total }),
    source: {
      variant: "random",
      basedOn: "E8_2025_MAJ_16",
    },
    tags: ["e8", "ulamki-zwykle", "dodawanie-odejmowanie", "zadanie-otwarte"],
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") return createOriginalTask();
  return createRandomTask();
}

function TextSetupInput({ value, onChange, disabled }) {
  return (
    <input
      type="text"
      value={value || ""}
      onChange={(event) => onChange(event.target.value)}
      disabled={disabled}
      placeholder="np. 7/15 - (1/5 + 1/6)"
      style={{
        width: "100%",
        maxWidth: 420,
        padding: "10px 12px",
        borderRadius: 10,
        border: "1px solid rgba(255,255,255,0.45)",
        background: "#111",
        color: "#fff",
        fontSize: "1rem",
      }}
    />
  );
}

export function renderTask({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        Suma trzech ułamków zwykłych jest równa <ReadableInlineMath scale={1} math={fractionLatex(data.total)} />.
        Dwa pierwsze składniki tej sumy to <ReadableInlineMath scale={1} math={fractionLatex(data.first)} /> oraz{" "}
        <ReadableInlineMath scale={1} math={fractionLatex(data.second)} />.
      </p>
      <p>
        Uzasadnij, że trzeci składnik tej sumy można przedstawić w postaci ułamka
        zwykłego o liczniku <InlineMath math="1" />. Oblicz ten składnik.
      </p>
    </div>
  );
}

export function renderInput({ data }, userAnswer = {}, onChange, disabled) {
  const setField = (field) => (value) => {
    onChange({ ...(userAnswer || {}), [field]: value });
  };

  return (
    <div style={{ display: "grid", gap: 16, textAlign: "left" }}>
      <div>
        <div style={{ fontWeight: 700, marginBottom: 6 }}>
          Krok 1. Zapisz wyrażenie albo równanie prowadzące do obliczenia trzeciego składnika.
        </div>
        <TextSetupInput value={userAnswer.setup} onChange={setField("setup")} disabled={disabled} />
      </div>
      <div>
        <div style={{ fontWeight: 700, marginBottom: 6 }}>
          Krok 2. Wpisz obliczony trzeci składnik.
        </div>
        <FractionInput
          label="x"
          value={userAnswer.result}
          onChange={setField("result")}
          disabled={disabled}
          placeholder="np. 1/10"
        />
      </div>
    </div>
  );
}

export function checkAnswer({ data }, userAnswer = {}) {
  const setupOk = setupLooksCorrect(userAnswer.setup, data);
  const result = parseFraction(rawValue(userAnswer.result));
  const resultOk = sameFraction(result, data.answer);

  return scoreStaged({
    resultOk,
    milestones: [setupOk],
    maxScore: data.maxPoints,
    parts: {
      "wyrażenie lub równanie": setupOk,
      "wynik po obliczeniach": resultOk,
    },
  });
}

export function renderAnswer({ data }) {
  return (
    <div style={{ textAlign: "left" }}>
      <div>
        Wyrażenie: <ReadableInlineMath scale={1} math={`${fractionLatex(data.total)}-(${fractionLatex(data.first)}+${fractionLatex(data.second)})`} />
      </div>
      <div>
        Trzeci składnik: <ReadableInlineMath scale={1} math={fractionLatex(data.answer)} />
      </div>
    </div>
  );
}

export function renderUserAnswer(userAnswer = {}) {
  const result = parseFraction(rawValue(userAnswer.result));
  return (
    <div style={{ textAlign: "left" }}>
      <div>Wyrażenie/równanie: {userAnswer.setup || <i>(brak)</i>}</div>
      <div>
        Wynik: {result ? <ReadableInlineMath scale={1} math={fractionLatex(result)} /> : <i>(brak)</i>}
      </div>
    </div>
  );
}

export function renderExplanation({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.75 }}>
      <p>
        <strong>Krok 1.</strong> Oznaczmy trzeci składnik przez <InlineMath math="x" />.
        Z treści zadania zapisujemy równanie:
      </p>
      <BlockMath math={`${fractionLatex(data.first)}+${fractionLatex(data.second)}+x=${fractionLatex(data.total)}`} />
      <p>
        <strong>Krok 2.</strong> Wyznaczamy brakujący składnik, czyli odejmujemy od sumy
        dwa znane składniki.
      </p>
      <BlockMath math={`x=${fractionLatex(data.total)}-\\left(${fractionLatex(data.first)}+${fractionLatex(data.second)}\\right)`} />
      <p>
        <strong>Krok 3.</strong> Sprowadzamy ułamki do wspólnego mianownika.
      </p>
      <BlockMath math={`${fractionLatex(data.total)}=${fractionLatex(data.totalExpanded)},\\quad ${fractionLatex(data.first)}=${fractionLatex(data.firstExpanded)},\\quad ${fractionLatex(data.second)}=${fractionLatex(data.secondExpanded)}`} />
      <p>
        <strong>Krok 4.</strong> Obliczamy różnicę.
      </p>
      <BlockMath math={`x=${fractionLatex(data.totalExpanded)}-\\left(${fractionLatex(data.firstExpanded)}+${fractionLatex(data.secondExpanded)}\\right)=${fractionLatex(data.totalExpanded)}-${fractionLatex(data.knownSumExpanded)}=${fractionLatex(data.answerExpanded)}=${fractionLatex(data.answer)}`} />
      <p>
        Trzeci składnik sumy można przedstawić jako ułamek zwykły o liczniku{" "}
        <InlineMath math="1" />, bo <ReadableInlineMath scale={1} math={`x=${fractionLatex(data.answer)}`} />.
      </p>
    </div>
  );
}
